// Generic week-planner adapter for saved views: a calendar block's rows →
// PlannerEvents for the D-styled WeekPlanner shell (hairline grid, pale
// chips, designed dark — replaces the old month-grid calendar in views).
//
// The planner components own the visuals; this file owns only the mapping:
//   - event type: the view entity maps onto a planner family when it has one
//     (task → task chip, meeting → meeting, deal → deadline outline), every
//     other entity renders as the neutral `generic` chip;
//   - href: the entity card route when the entity has one, otherwise the
//     entity's list page;
//   - overdue: a date before today — the view rows carry no status beyond
//     what their own filters already encoded.

import { useMemo, useState, type ReactElement } from 'react';
import dayjs from 'dayjs';

import { isEntityKey, type EntityKey } from '../../../views/catalog.js';
import type { CalendarResult } from '../../../views/result.js';
import { WeekPlanner } from '../../calendar/components/WeekPlanner.js';
import type { PlannerEvent, PlannerEventType } from '../../calendar/lib/plannerEvents.js';

/** Planner family per entity — entities without a family use `generic`. */
const ENTITY_EVENT_TYPE: Record<EntityKey, PlannerEventType> = {
  deal: 'deadline',
  document: 'generic',
  company: 'generic',
  contact: 'generic',
  task: 'task',
  note: 'generic',
  meeting: 'meeting',
  activity: 'generic',
  memory: 'generic',
};

/** Card route per entity; entities without a card page fall back to lists. */
const ENTITY_HREF: Record<EntityKey, string> = {
  deal: '/deals',
  document: '/documents',
  company: '/companies',
  contact: '/contacts',
  task: '/tasks',
  note: '/notes',
  meeting: '/meetings',
  activity: '/dashboard',
  memory: '/memory',
};

function eventHref(entity: string, id: string): string {
  const base = isEntityKey(entity) ? (ENTITY_HREF[entity] ?? null) : null;
  const fallback = base ?? '/dashboard';
  return id !== '' ? `${fallback}/${id}` : fallback;
}

function entityTypeOf(entity: string): PlannerEventType {
  return isEntityKey(entity) ? (ENTITY_EVENT_TYPE[entity] ?? 'generic') : 'generic';
}

function minutesOfDay(value: dayjs.Dayjs): number {
  return value.hour() * 60 + value.minute();
}

function toPlannerEvent(entity: string, event: CalendarResult['events'][number]): PlannerEvent {
  const start = dayjs(event.start);
  const dateKey = start.format('YYYY-MM-DD');
  const hasTime = start.hour() !== 0 || start.minute() !== 0;
  const todayKey = dayjs().format('YYYY-MM-DD');

  return {
    id: `${entity}:${event.id}`,
    eventType: entityTypeOf(entity),
    title: event.title,
    entityLabel: null,
    whoLabel: null,
    amountLabel: null,
    dateKey,
    timeLabel: hasTime ? start.format('HH:mm') : null,
    timeMinutes: hasTime ? minutesOfDay(start) : null,
    done: false,
    overdue: dateKey < todayKey,
    href: eventHref(entity, event.id),
    taskId: null,
  };
}

export function ViewPlannerCalendar({ block }: { block: CalendarResult }): ReactElement {
  const [cursor, setCursor] = useState(() => dayjs());

  const events = useMemo(
    () => block.events.map((event) => toPlannerEvent(block.entity, event)),
    [block.entity, block.events],
  );
  const availableTypes = useMemo(
    () => [...new Set(events.map((event) => event.eventType))],
    [events],
  );

  // Click routing is built into the shell: cards navigate to PlannerEvent.href.
  return (
    <WeekPlanner
      events={events}
      cursor={cursor}
      onCursorChange={setCursor}
      availableTypes={availableTypes}
    />
  );
}
